import { Injectable } from '@angular/core';

import { Subscription, map } from 'rxjs';
import { MapApi } from '../../api/map.api';
import { BrowseFilter } from '../../models/filter';
import { Sport } from '../../models/sport';
import { Store } from '../../../../shared/store';
import { MapState } from './map.state';
import { VideoSection } from '@kinomap-dev/ng-ui-lib';

const mapState = new MapState();

@Injectable({ providedIn: 'root' })
export class MapStore extends Store<MapState> {
  videos$ = this.state$.pipe(map((s) => s.videos));
  totalAvailableVideos$ = this.state$.pipe(map((s) => s.totalAvailableVideos));
  isLoading$ = this.state$.pipe(map((s) => s.isLoading));
  areVideosComplete$ = this.state$.pipe(map((s) => s.areVideosComplete));
  requestSubscription$ = this.state$.pipe(map((s) => s.requestSubscription));

  constructor(private mapApi: MapApi) {
    super(mapState);
  }

  // SET DATA

  setVideos(nextVideos: VideoSection[]): void {
    this.setState({ ...this.state, videos: nextVideos });
  }

  setTotalAvailableVideos(nextTotalAvailableVideos: number): void {
    this.setState({
      ...this.state,
      totalAvailableVideos: nextTotalAvailableVideos,
    });
  }

  setIsLoading(nextIsLoading: boolean): void {
    this.setState({ ...this.state, isLoading: nextIsLoading });
  }

  setAreVideosComplete(nextAreVideosComplete: boolean): void {
    this.setState({ ...this.state, areVideosComplete: nextAreVideosComplete });
  }

  setRequestSubscription(nextRequestSubscription: Subscription): void {
    this.setState({
      ...this.state,
      requestSubscription: nextRequestSubscription,
    });
  }

  // GET DATA FROM API

  async getVideos({
    sport,
    filters,
    centerAndRadius,
    isNextPage,
    id,
    path,
  }: {
    sport?: Sport;
    filters?: BrowseFilter[];
    centerAndRadius?: {
      center: {
        lat: number;
        lng: number;
      };
      radius: number;
    };
    isNextPage: boolean;
    id?: number;
    path?: 'playlist' | 'myVideos' | 'videos';
  }): Promise<void> {
    if (isNextPage && this.state.areVideosComplete) {
      return;
    }

    if (this.state.requestSubscription) {
      this.state.requestSubscription.unsubscribe();
    }

    if (!isNextPage && this.state.videos.length > 0) {
      this.clear();
    }

    this.setIsLoading(true);

    const sub: Subscription = this.mapApi
      .getVideos$({
        sport,
        filters,
        latitude: centerAndRadius?.center.lat,
        longitude: centerAndRadius?.center.lng,
        radius: Math.round(centerAndRadius?.radius),
        isNextPage,
        id,
        path,
      })
      .subscribe({
        next: (result: { videos: VideoSection[]; total: number }) => {
          const newVideos: VideoSection[] = isNextPage
            ? this.getVideosWithoutDuplicate(result.videos)
            : result.videos;
          this.setVideos(newVideos);
          this.setTotalAvailableVideos(result.total);
          this.setIsLoading(false);
          this.setAreVideosComplete(this.mapApi.isLastPageLoaded);
        },
        error: () => {
          this.setVideos([]);
          this.setTotalAvailableVideos(0);
          this.setIsLoading(false);
          this.setAreVideosComplete(true);
        },
      });

    this.setRequestSubscription(sub);
  }

  private getVideosWithoutDuplicate(
    fetchedVideos: VideoSection[]
  ): VideoSection[] {
    const newVideosWithDuplicates: VideoSection[] = [
      ...this.state.videos,
      ...fetchedVideos,
    ];
    const obj: {} = {};
    newVideosWithDuplicates.forEach(
      (content: VideoSection) => (obj[content.id] = content)
    );
    return [
      ...new Set(
        newVideosWithDuplicates.map((video: VideoSection) => video.id)
      ),
    ].map((id) => obj[id]);
  }

  clear(): void {
    this.setState({
      videos: [],
      totalAvailableVideos: 0,
      isLoading: false,
      areVideosComplete: false,
      requestSubscription: undefined,
    });
  }
}
